// File: frontend/src/pages/idcards/IDCardGenerator.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useSchool } from '../../context/SchoolContext';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/axios';

const CLASSES_LIST = [
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11', 'Class 12'
];

const IDCardGenerator = () => {
  const { school } = useSchool();
  const { user } = useAuth();
  
  // Selection Filters
  const [selectedClass, setSelectedClass] = useState('Class 10');
  const [selectedStudentId, setSelectedStudentId] = useState('all');
  
  // Data state
  const [studentsList, setStudentsList] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [schoolData, setSchoolData] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [error, setError] = useState('');

  const printAreaRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const isUnlocked = isSuperAdmin || school?.feature_id_cards !== 0;

  /**
   * Fetch students dropdown list whenever selectedClass changes
   */
  useEffect(() => {
    if (!isUnlocked) return;
    const fetchClassStudents = async () => {
      try {
        setFetchingStudents(true);
        const res = await api.get('/api/v1/students', {
          params: { class_id: selectedClass, limit: 100 }
        });
        if (res.data?.success) {
          setStudentsList(res.data.data || []);
          setSelectedStudentId('all');
        }
      } catch (err) {
        console.error('Error fetching students list:', err);
      } finally {
        setFetchingStudents(false);
      }
    };

    fetchClassStudents();
  }, [selectedClass, isUnlocked]);

  /**
   * Generate ID card payload from backend
   */
  const handleGenerateCards = async () => {
    try {
      setLoading(true);
      setError('');

      if (selectedStudentId === 'all') {
        const res = await api.get(`/api/v1/idcards/class/${encodeURIComponent(selectedClass)}`);
        if (res.data?.success) {
          setSchoolData(res.data.data.school);
          setCardData(res.data.data.students || []);
        } else {
          setError(res.data?.message || 'Failed to generate ID Cards');
        }
      } else {
        const res = await api.get(`/api/v1/idcards/${selectedStudentId}`);
        if (res.data?.success) {
          setSchoolData(res.data.data.school);
          setCardData([res.data.data.student]);
        } else {
          setError(res.data?.message || 'Failed to generate ID Card');
        }
      }
    } catch (err) {
      console.error('Error generating ID cards:', err);
      setError(err.response?.data?.message || 'Error generating ID cards');
    } finally {
      setLoading(false);
    }
  };

  // Generate on initial render
  useEffect(() => {
    if (isUnlocked) {
      handleGenerateCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnlocked]);

  /**
   * Print Handler
   */
  const handlePrint = () => {
    window.print();
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-8 flex items-center justify-center">
        <div className="bg-slate-800 text-white rounded-3xl p-8 shadow-xl text-center space-y-4 max-w-2xl mx-auto border border-purple-500/30">
          <div className="w-16 h-16 rounded-3xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-3xl font-extrabold mx-auto border border-amber-500/30">
            🪪
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight">ID Card Generator - Locked Pro Feature</h2>
          <p className="text-sm text-slate-300">
            Printable Student & Staff ID Badges are not active for <strong>{school?.name || 'your school portal'}</strong>.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://wa.me/923171818108?text=Hello%20Waleed%20Technologies,%20I%20want%20to%20upgrade%20my%20school%20plan%20tier%20to%20unlock%20ID%20Card%20Generator%20Pro%20feature"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-lg transition"
            >
              <span>💬</span>
              <span>Upgrade via WhatsApp (+92 317 1818108)</span>
            </a>

            <a
              href="mailto:waleed.khalid111@gmail.com?subject=Upgrade to Unlock ID Card Generator Feature"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition"
            >
              <span>✉️</span>
              <span>Upgrade via Email</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Printable CSS Rules (85.6mm x 53.98mm credit card exact specs) */}
        <style>{`
          @media print {
            body {
              background: white !important;
              color: black !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .no-print, header, nav, sidebar, footer {
              display: none !important;
            }
            .print-container {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 15px !important;
              padding: 10px !important;
              background: white !important;
            }
            .id-card-wrap {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
          }
        `}</style>

        {/* --- HEADER BANNER --- */}
        <div className="bg-gradient-to-r from-purple-900/50 via-slate-800 to-indigo-900/50 p-6 rounded-2xl border border-slate-700/60 shadow-xl backdrop-blur-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 no-print">
          <div>
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-500/20 text-purple-400 rounded-xl border border-purple-500/30 font-bold text-xl">
                📇
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">Student ID Card Generator</h1>
                <p className="text-xs sm:text-sm text-slate-400">
                  Generate, preview, and print high-resolution credit-card-sized student identity cards
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handlePrint}
              disabled={cardData.length === 0}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold rounded-xl shadow-lg transition flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print All Cards ({cardData.length})</span>
            </button>
          </div>
        </div>

        {/* --- FILTER CONTROL BAR --- */}
        <div className="bg-slate-800/80 p-5 rounded-2xl border border-slate-700/60 shadow-md no-print">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            
            {/* Class Dropdown */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Select Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {CLASSES_LIST.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            {/* Individual Student Filter */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">Select Student</label>
              <select
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                disabled={fetchingStudents}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
              >
                <option value="all">-- All Students in {selectedClass} ({studentsList.length}) --</option>
                {studentsList.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.student_name} ({st.student_id}) - Roll #{st.roll_no || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            {/* Generate Action Button */}
            <div>
              <button
                onClick={handleGenerateCards}
                disabled={loading}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-xl shadow-lg transition flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <span>Generating Cards...</span>
                ) : (
                  <>
                    <span>✨ Generate Cards</span>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>

        {/* --- ERROR ALERT --- */}
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-sm no-print">
            ⚠️ {error}
          </div>
        )}

        {/* --- ID CARDS PREVIEW / PRINT AREA --- */}
        {cardData.length === 0 ? (
          <div className="bg-slate-800/40 p-12 rounded-2xl border border-dashed border-slate-700 text-center no-print">
            <p className="text-slate-300 font-semibold mb-1">No ID Cards Generated</p>
            <p className="text-xs text-slate-500">Select a class and click Generate Cards above.</p>
          </div>
        ) : (
          <div ref={printAreaRef} className="print-container space-y-8 print:space-y-0">
            {cardData.map((student) => (
              <div key={student.id} className="id-card-wrap flex flex-col md:flex-row gap-6 items-center justify-center print:inline-flex print:gap-3">
                
                {/* --- FRONT SIDE OF ID CARD (85.6mm x 53.98mm ratio -> 340px x 214px) --- */}
                <div
                  className="w-[340px] h-[214px] bg-slate-900 rounded-2xl border-2 border-purple-500/30 shadow-2xl overflow-hidden flex flex-col justify-between relative print:border-slate-800 print:shadow-none shrink-0"
                  style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)' }}
                >
                  {/* Header Bar */}
                  <div className="bg-purple-600 px-3 py-2 flex items-center justify-between border-b border-purple-400/30">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center font-bold text-white text-xs">
                        🏫
                      </div>
                      <div>
                        <h2 className="text-[11px] font-extrabold text-white leading-none tracking-tight uppercase">
                          {schoolData?.name || school?.name || 'SCHOOL PORTAL'}
                        </h2>
                        <p className="text-[8px] text-purple-200 leading-none mt-0.5">
                          STUDENT IDENTITY CARD
                        </p>
                      </div>
                    </div>
                    <span className="text-[9px] font-bold text-purple-100 bg-purple-700/60 px-1.5 py-0.5 rounded uppercase">
                      {schoolData?.session_year || '2025-2026'}
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="p-3 flex items-center justify-between flex-1 gap-3">
                    
                    {/* Left Details */}
                    <div className="space-y-1 flex-1">
                      <h3 className="text-sm font-black text-white leading-tight uppercase tracking-tight">
                        {student.student_name}
                      </h3>

                      <div className="text-[10px] space-y-0.5 text-slate-300 font-medium">
                        <p>
                          <span className="text-slate-400">ID:</span>{' '}
                          <strong className="text-purple-300 font-mono">{student.student_id}</strong>
                        </p>
                        <p>
                          <span className="text-slate-400">Class:</span>{' '}
                          <strong className="text-slate-100">{student.class_id} {student.section ? `(${student.section})` : ''}</strong>
                        </p>
                        <p>
                          <span className="text-slate-400">Roll No:</span>{' '}
                          <strong className="text-slate-100">{student.roll_no || 'N/A'}</strong>
                        </p>
                        <p>
                          <span className="text-slate-400">Father:</span>{' '}
                          <strong className="text-slate-200">{student.father_name || 'N/A'}</strong>
                        </p>
                        <p>
                          <span className="text-slate-400">Blood Group:</span>{' '}
                          <strong className="text-red-400">{student.blood_group || 'O+'}</strong>
                        </p>
                      </div>
                    </div>

                    {/* Right Student Photo */}
                    <div className="w-[72px] h-[88px] rounded-xl border-2 border-purple-400/40 bg-slate-800 overflow-hidden shrink-0 shadow-md flex items-center justify-center">
                      {student.photo ? (
                        <img
                          src={getImageUrl(student.photo)}
                          alt={student.student_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center">
                          <span className="text-2xl">👤</span>
                          <span className="block text-[8px] text-slate-400 uppercase font-bold">Photo</span>
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Bottom Accent Bar */}
                  <div className="bg-slate-950 px-3 py-1 border-t border-slate-800 flex justify-between items-center text-[8px] text-slate-400">
                    <span>Authorized Student Signature</span>
                    <span className="font-mono text-purple-400 font-bold">FRONT SIDE</span>
                  </div>
                </div>

                {/* --- BACK SIDE OF ID CARD --- */}
                <div
                  className="w-[340px] h-[214px] bg-slate-900 rounded-2xl border-2 border-slate-700/60 shadow-2xl overflow-hidden flex flex-col justify-between relative print:border-slate-800 print:shadow-none shrink-0"
                  style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)' }}
                >
                  {/* Header */}
                  <div className="bg-slate-800 px-3 py-1.5 border-b border-slate-700 flex justify-between items-center">
                    <span className="text-[10px] font-bold text-slate-200 uppercase">Emergency Contact & Info</span>
                    <span className="text-[8px] font-bold text-slate-400">SchoolMS</span>
                  </div>

                  {/* Body Content */}
                  <div className="p-3 space-y-2 text-[9px] text-slate-300 flex-1">
                    <div>
                      <p className="font-bold text-slate-100">{schoolData?.name || school?.name}</p>
                      <p className="text-slate-400 text-[8px]">{schoolData?.address || 'Main Campus, Education City'}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800">
                      <div>
                        <span className="text-slate-400 block text-[8px]">School Phone</span>
                        <strong className="text-slate-200">{schoolData?.phone || '+92-300-1234567'}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[8px]">Emergency Phone</span>
                        <strong className="text-purple-300">{student.emergency_contact || student.phone || '+92-300-1234567'}</strong>
                      </div>
                    </div>

                    <div className="pt-1">
                      <p className="text-[8px] text-slate-400 italic">
                        If found, please return this card to the school administration at above address.
                      </p>
                    </div>

                    {/* Barcode Element */}
                    <div className="pt-1 flex items-center justify-between">
                      <div className="font-mono text-center px-2 py-1 bg-white text-black font-black text-xs tracking-widest rounded border border-slate-300">
                        ||| | |||| | ||| {student.student_id}
                      </div>
                      <div className="text-[8px] text-right text-slate-400">
                        Valid Until: <strong className="text-purple-300">{schoolData?.session_year}</strong>
                      </div>
                    </div>

                  </div>

                  {/* Footer Bar */}
                  <div className="bg-slate-950 px-3 py-1 border-t border-slate-800 flex justify-between items-center text-[8px] text-slate-400">
                    <span>Principal Signature</span>
                    <span className="font-mono text-slate-400 font-bold">BACK SIDE</span>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default IDCardGenerator;
